import { type ButtonHandle } from "@progress/kendo-react-buttons";
import { Popup } from "@progress/kendo-react-popup";
import {
    createContext,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ComponentProps,
    type MouseEvent as ReactMouseEvent,
    type ReactNode
} from "react";
import ActionButton from "./actionButton";

type ActionButtonProps = ComponentProps<typeof ActionButton>;

type ActionButtonMenuContextValue = {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    anchorElement: HTMLElement | null;
    setAnchorElement: (element: HTMLElement | null) => void;
};

const ActionButtonMenuContext = createContext<ActionButtonMenuContextValue | null>(null);

const useActionButtonMenuContext = () => {
    const context = useContext(ActionButtonMenuContext);

    if (!context) {
        throw new Error("ActionButtonMenu components must be used within ActionButtonMenu");
    }

    return context;
};

type ActionButtonMenuProps = {
    children: ReactNode;
};

const ActionButtonMenuRoot = ({ children }: ActionButtonMenuProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

    return (
        <ActionButtonMenuContext.Provider value={{ isOpen, setIsOpen, anchorElement, setAnchorElement }}>
            {children}
        </ActionButtonMenuContext.Provider>
    );
};

const ActionButtonMenuTrigger = ({
    onClick,
    ...rest
}: ActionButtonProps) => {
    const { isOpen, setIsOpen, setAnchorElement } = useActionButtonMenuContext();
    const triggerRef = useRef<ButtonHandle | null>(null);

    useLayoutEffect(() => {
        setAnchorElement(triggerRef.current?.element ?? null);
    }, [isOpen, setAnchorElement]);

    const handleClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        setIsOpen(!isOpen);
        onClick?.(event);
    };

    return (
        <ActionButton
            {...rest}
            ref={triggerRef}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            onClick={handleClick}
        />
    );
};

type ActionButtonMenuContentProps = {
    children: ReactNode;
    className?: string;
};

const ActionButtonMenuContent = ({
    children,
    className = "dropdown-menu show p-2 position-static w-100 border-0 rounded-0 shadow-none"
}: ActionButtonMenuContentProps) => {
    const { isOpen, setIsOpen, anchorElement } = useActionButtonMenuContext();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        window.addEventListener("keydown", handleEscape);

        return () => {
            window.removeEventListener("keydown", handleEscape);
        };
    }, [isOpen, setIsOpen]);

    return (
        <Popup
            show={isOpen}
            anchor={anchorElement}
            anchorAlign={{ horizontal: "right", vertical: "bottom" }}
            popupAlign={{ horizontal: "right", vertical: "top" }}
            popupClass="rounded-3 shadow-sm overflow-hidden"
            margin={{ horizontal: 0, vertical: 8 }}
            onMouseDownOutside={event => {
                if (!event.isAnchorClicked) {
                    setIsOpen(false);
                }
            }}
        >
            <div
                className={className}
                role="menu"
                onClickCapture={() => setIsOpen(false)}
            >
                {children}
            </div>
        </Popup>
    );
};

const ActionButtonMenu = Object.assign(ActionButtonMenuRoot, {
    Trigger: ActionButtonMenuTrigger,
    Content: ActionButtonMenuContent
});

export default ActionButtonMenu;
